
import React from 'react';

export default function ImpactSection() {
  const stats = [
    { number: '200+', label: 'Conditions Linked', description: 'Diseases connected to oxygen deficiency' },
    { number: '1,000+', label: 'Beneficiaries', description: 'Lives directly impacted by our programs' },
    { number: '15', label: 'Districts', description: 'Operational districts in pipeline' },
    { number: '50+', label: 'Health Centers', description: 'PHCs and ASHA partnerships' }
  ];

  const testimonials = [
    {
      quote: "After 10 years of struggling with COPD, the oxygen capsules have given me my life back. I can walk without breathlessness and sleep peacefully through the night.",
      author: "Rajesh Kumar",
      role: "COPD Patient",
      location: "Delhi",
      avatar: 'https://public.readdy.ai/ai/img_res/8294486941cd7fb4cb752fb7e5b13b78.jpg'
    },
    {
      quote: "PCOD symptoms were controlling my life. The oxygen therapy helped regulate my hormones naturally. My periods are regular and acne has cleared up significantly.",
      author: "Priya Joshi",
      role: "PCOD Recovery",
      location: "Pune",
      avatar: 'https://readdy.ai/api/search-image?query=Young%20Indian%20woman%20with%20clear%20glowing%20skin%2C%20PCOD%20recovery%2C%20healthy%20appearance%2C%20confident%20smile%2C%20hormonal%20balance%20success%20story&width=100&height=100&seq=priya-home-avatar&orientation=squarish'
    },
    {
      quote: "Psoriasis patches covered 40% of my body. The oxygen sprays and capsules together cleared 80% of my skin. I can wear short sleeves again!",
      author: "Arjun Mehta",
      role: "Psoriasis Patient",
      location: "Chennai",
      avatar: 'https://readdy.ai/api/search-image?query=Young%20Indian%20man%20with%20clear%20healthy%20skin%2C%20psoriasis%20recovery%2C%20confident%20expression%2C%20skin%20healing%20success%20story%2C%20medical%20breakthrough&width=100&height=100&seq=arjun-home-avatar&orientation=squarish'
    }
  ];

  return (
    <section className="py-20 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#111111] mb-6">
            Impact & Testimonials
          </h2>
          <p className="text-xl text-gray-700 leading-relaxed">
            Real stories, measurable impact. See how we're transforming lives 
            through oxygen awareness and accessible healthcare solutions.
          </p>
        </div>

        {/* Stats Strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {stats.map((stat, index) => (
            <div key={index} className="text-center bg-white rounded-2xl p-6 shadow-lg">
              <div className="text-3xl md:text-4xl font-bold text-[#12B6B6] mb-2">
                {stat.number}
              </div>
              <div className="text-lg font-semibold text-[#111111] mb-1">
                {stat.label}
              </div>
              <div className="text-sm text-gray-600">
                {stat.description}
              </div>
            </div>
          ))}
        </div>

        {/* Testimonials */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <div key={index} className="bg-white rounded-2xl p-8 shadow-lg">
              {/* Quote */}
              <div className="mb-6">
                <i className="ri-double-quotes-l text-3xl text-[#12B6B6] mb-4 block"></i>
                <p className="text-gray-700 leading-relaxed italic">
                  "{testimonial.quote}"
                </p>
              </div>

              {/* Author */}
              <div className="flex items-center space-x-4">
                <img 
                  src={testimonial.avatar} 
                  alt={testimonial.author}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <div className="font-semibold text-[#111111]">{testimonial.author}</div>
                  <div className="text-sm text-[#12B6B6]">{testimonial.role}</div>
                  <div className="text-xs text-gray-500">{testimonial.location}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Impact Visualization */}
        <div className="mt-16 bg-white rounded-3xl p-8 shadow-lg">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-[#111111] mb-4">
              Your Donation Creates Ripple Effects
            </h3>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Every contribution extends beyond individual families, creating community-wide 
              awareness and building sustainable healthcare infrastructure.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#12B6B6]/10 rounded-2xl flex items-center justify-center">
                <i className="ri-user-heart-line text-3xl text-[#12B6B6]"></i>
              </div>
              <h4 className="text-lg font-semibold text-[#111111] mb-2">Direct Impact</h4>
              <p className="text-sm text-gray-600">Immediate health improvements in supported families</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#12B6B6]/10 rounded-2xl flex items-center justify-center">
                <i className="ri-team-line text-3xl text-[#12B6B6]"></i>
              </div>
              <h4 className="text-lg font-semibold text-[#111111] mb-2">Community Spread</h4>
              <p className="text-sm text-gray-600">Knowledge sharing within local communities</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#12B6B6]/10 rounded-2xl flex items-center justify-center">
                <i className="ri-hospital-line text-3xl text-[#12B6B6]"></i>
              </div>
              <h4 className="text-lg font-semibold text-[#111111] mb-2">System Strengthening</h4>
              <p className="text-sm text-gray-600">Enhanced PHC and ASHA worker capabilities</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
